import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { authSupabase, supabaseUrl } from '../../services/supabase/supabaseClient.js';

const supabaseReady = Boolean(authSupabase);
const oauthClientId = process.env.REACT_APP_SUPABASE_OAUTH_CLIENT_ID;
const oauthRedirectUri = process.env.REACT_APP_SUPABASE_OAUTH_REDIRECT_URI;
const oauthScope = process.env.REACT_APP_SUPABASE_OAUTH_SCOPE || 'profile email';

const getRedirectUri = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const fallback = origin ? `${origin}/oauth/callback` : '';
  if (!oauthRedirectUri) return fallback;
  try {
    const envUrl = new URL(oauthRedirectUri);
    if (origin && envUrl.origin === origin) return oauthRedirectUri;
  } catch {
    return fallback || oauthRedirectUri;
  }
  return fallback || oauthRedirectUri;
};

const base64UrlEncode = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const createCodeVerifier = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

const createCodeChallenge = async (verifier) => {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

const OAuthConsent = () => {
  const location = useLocation();
  const authorizationId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('authorization_id') || '';
  }, [location.search]);
  const hasAuthorizationId = Boolean(authorizationId);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [autoStart, setAutoStart] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ready, setReady] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [approving, setApproving] = useState(false);
  const [denying, setDenying] = useState(false);

  const loadDetails = useCallback(async () => {
    setLoading(true);
    setError('');
    setDetails(null);
    setReady(false);

    if (!authorizationId) {
      setLoading(false);
      return;
    }
    if (!authSupabase) {
      setError('Supabase no está configurado en este entorno.');
      setLoading(false);
      return;
    }

    const { data: sessionData, error: sessionError } = await authSupabase.auth.getSession();
    if (sessionError || !sessionData?.session) {
      if (sessionError?.message) {
        setError(sessionError.message);
      } else {
        setError('Debes iniciar sesión en Supabase para continuar.');
      }
      setLoading(false);
      return;
    }

    const { data, error: detailsError } = await authSupabase.auth.oauth.getAuthorizationDetails(authorizationId);
    if (detailsError) {
      setError(detailsError.message || 'Error al cargar la autorización.');
      setLoading(false);
      return;
    }

    if (data?.redirect_url) {
      window.location.assign(data.redirect_url);
      return;
    }

    setDetails(data);
    setReady(true);
    setLoading(false);
  }, [authorizationId]);

  useEffect(() => {
    if (hasAuthorizationId) {
      loadDetails();
    }
  }, [hasAuthorizationId, loadDetails]);

  const handleStart = async () => {
    if (!supabaseReady) {
      setError('Supabase no está configurado en este entorno.');
      return;
    }
    if (!oauthClientId) {
      setError('Falta REACT_APP_SUPABASE_OAUTH_CLIENT_ID en el entorno.');
      return;
    }
    setStarting(true);
    setError('');
    const verifier = createCodeVerifier();
    const challenge = await createCodeChallenge(verifier);
    sessionStorage.setItem('supabase_oauth_code_verifier', verifier);
    const redirectUri = getRedirectUri();
    const authUrl = `${supabaseUrl.replace(/\/$/, '')}/auth/v1/oauth/authorize?response_type=code&client_id=${encodeURIComponent(
      oauthClientId
    )}&redirect_uri=${encodeURIComponent(redirectUri)}&code_challenge=${encodeURIComponent(
      challenge
    )}&code_challenge_method=S256&scope=${encodeURIComponent(oauthScope)}`;
    window.location.assign(authUrl);
  };

  useEffect(() => {
    if (autoStart || starting || hasAuthorizationId || !supabaseReady) return;
    setAutoStart(true);
    handleStart();
  }, [autoStart, starting, hasAuthorizationId, supabaseReady, handleStart]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!authSupabase) {
      setError('Supabase no está configurado en este entorno.');
      return;
    }
    setLoggingIn(true);
    setError('');
    const { error: loginError } = await authSupabase.auth.signInWithPassword({
      email,
      password,
    });
    if (loginError) {
      setError(loginError.message || 'Error al iniciar sesión.');
      setLoggingIn(false);
      return;
    }
    setLoggingIn(false);
    loadDetails();
  };

  const handleApprove = async () => {
    if (!authorizationId || !authSupabase) return;
    setApproving(true);
    setError('');
    const { data, error: approveError } = await authSupabase.auth.oauth.approveAuthorization(authorizationId);
    if (approveError) {
      setError(approveError.message || 'Error al aprobar.');
      setApproving(false);
      return;
    }
    if (data?.redirect_url) {
      window.location.assign(data.redirect_url);
      return;
    }
    setApproving(false);
  };

  const handleDeny = async () => {
    if (!authorizationId || !authSupabase) return;
    setDenying(true);
    setError('');
    const { data, error: denyError } = await authSupabase.auth.oauth.denyAuthorization(authorizationId);
    if (denyError) {
      setError(denyError.message || 'Error al rechazar.');
      setDenying(false);
      return;
    }
    if (data?.redirect_url) {
      window.location.assign(data.redirect_url);
      return;
    }
    setDenying(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-4">
        <h1 className="text-2xl font-semibold">Consentimiento OAuth</h1>
        {error ? (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        ) : null}
        {!supabaseReady ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-2">
            <div>Supabase no está disponible en este entorno.</div>
            <div>Define REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_KEY en Vercel y vuelve a desplegar.</div>
            <div>authorization_id: {authorizationId || 'no encontrado'}</div>
          </div>
        ) : null}
        {loading ? <div className="text-sm text-slate-600">Cargando autorización...</div> : null}
        {!loading && !ready && !hasAuthorizationId ? (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">No hay authorization_id. Inicia el flujo OAuth desde aquí.</div>
            <button
              type="button"
              className="rounded bg-slate-900 px-4 py-2 text-sm text-white"
              onClick={handleStart}
              disabled={starting}
            >
              {starting ? 'Iniciando...' : 'Iniciar autorización'}
            </button>
          </div>
        ) : null}
        {!loading && !ready && hasAuthorizationId ? (
          <form className="space-y-3" onSubmit={handleLogin}>
            <div className="text-sm text-slate-600">Inicia sesión en Supabase para continuar.</div>
            <div className="space-y-1">
              <label className="text-sm text-slate-700" htmlFor="oauth-email">
                Email
              </label>
              <input
                id="oauth-email"
                type="email"
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-slate-700" htmlFor="oauth-password">
                Contraseña
              </label>
              <input
                id="oauth-password"
                type="password"
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="rounded bg-slate-900 px-4 py-2 text-sm text-white"
              disabled={loggingIn}
            >
              {loggingIn ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
        ) : null}
        {details && ready ? (
          <div className="space-y-4 rounded border border-slate-200 p-4">
            <div className="text-sm">
              <span className="font-medium">Aplicación:</span> {details?.client?.client_name || 'Desconocida'}
            </div>
            <div className="text-sm">
              <span className="font-medium">Usuario:</span> {details?.user?.email || 'Sin email'}
            </div>
            <div className="text-sm">
              <span className="font-medium">Scopes:</span> {details?.scope || ''}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded bg-emerald-600 px-4 py-2 text-sm text-white"
                onClick={handleApprove}
                disabled={approving}
              >
                {approving ? 'Aprobando...' : 'Aprobar'}
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm"
                onClick={handleDeny}
                disabled={denying}
              >
                {denying ? 'Rechazando...' : 'Rechazar'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OAuthConsent;
